import { Trans, useTranslation } from 'react-i18next';
import React, { useMemo, useCallback } from 'react';

import { Button, L2PayModal, ModalStepProps, UserAvatar } from '@/components';
import { MINT_ID_RECEIVER } from '@/consts';
import { useSwitch, usePostWeb3IdGift, Web3IdGiftOrder, useCurrentAddressInfo } from '@/hooks';
import { ImgRcSendGift } from '@/assets/pro';
import { useCurrentUserId, formatNum, shortenAddress, openNewWindow } from '@/utils';
import style from './index.module.scss';
import { useUserSetting } from '@/views/Settings/hooks';

export const GiftWeb3IdEntry = ({ receiverId }: { receiverId: string }) => {
  const { t } = useTranslation();

  const { on, turnOn, turnOff } = useSwitch(false);
  return (
    <div>
      <div className={style.giftWeb3IdEntry} onClick={turnOn}>
        <ImgRcSendGift width={16} height={16} />
        <span className={style.info}>{t('GiftWeb3IdEntry.qmwez1')}</span>
      </div>
      {on ? <GiftWeb3IdModal turnOff={turnOff} receiverId={receiverId} /> : null}
    </div>
  );
};

export const GiftWeb3IdPendingStatus = ({
  giftOrder,
  onMintClick,
}: {
  giftOrder: Web3IdGiftOrder;
  onMintClick?: () => void;
}) => {
  const { t } = useTranslation();

  const { address } = useCurrentAddressInfo();
  const isCurrentUser = address === giftOrder?.receiver.id;

  const buyerUserInfo = giftOrder.buyer;
  const enterBuyerProfile = useCallback(() => {
    openNewWindow(`/profile/${buyerUserInfo?.id}`);
  }, [buyerUserInfo?.id]);

  return giftOrder?.is_minted ? null : (
    <div className={style.giftWeb3IdPendingStatus}>
      <UserAvatar data={buyerUserInfo?.desc} size={20} userCardPopup onClick={enterBuyerProfile} />{' '}
      {isCurrentUser ? (
        <>
          <Trans i18nKey="GiftWeb3IdEntry.dsmkn3" values={{ user: buyerUserInfo?.web3_id }}>
            <span className={style.userName} onClick={enterBuyerProfile}>
              {buyerUserInfo?.web3_id}
            </span>
            <span className={style.giftInfo}> has gifted you an ID!</span>
          </Trans>
          {onMintClick ? (
            <span className={style.setButton} onClick={onMintClick}>
              {t('GiftWeb3IdEntry.1eqjtvk')}
            </span>
          ) : null}
        </>
      ) : (
        <Trans i18nKey="GiftWeb3IdEntry.bz5mvl" values={{ user: buyerUserInfo?.web3_id }}>
          <span className={style.userName} onClick={enterBuyerProfile}>
            {buyerUserInfo?.web3_id}
          </span>
          <span className={style.giftInfo}> has gifted this user an ID!</span>
        </Trans>
      )}
    </div>
  );
};

const GiftWeb3IdModal = ({ turnOff, receiverId }: { turnOff: () => void; receiverId: string }) => {
  const currentAddress = useCurrentUserId();
  const { mutateAsync } = usePostWeb3IdGift(receiverId);
  const { data: userSetting } = useUserSetting();
  const totalAmount = useMemo(() => userSetting?.user_paid_mint_price || 0, [userSetting]);
  const realTotalAmount = useMemo(() => totalAmount / 10000, [totalAmount]);

  const txParams = useMemo(
    () =>
      totalAmount && currentAddress
        ? {
            price: totalAmount,
            message: `Pay $${formatNum(realTotalAmount)} to gift a Web3 ID to user ${shortenAddress(
              receiverId,
            )}`,
            senderAddr: currentAddress,
            receiverAddr: MINT_ID_RECEIVER,
          }
        : undefined,
    [currentAddress, realTotalAmount, receiverId, totalAmount],
  );

  const payTitle = useMemo(
    () => (
      <div className={style.payTitle}>
        <Trans
          i18nKey="GiftWeb3IdEntry.rqy8ma"
          values={{
            amount: `$${formatNum(realTotalAmount)}`,
            receiver: shortenAddress(receiverId),
          }}>
          Pay
          <span className={style.payTitleWeight} />
          to gift a Web3 ID to user
          <span className={style.payTitleWeight} />
        </Trans>
      </div>
    ),
    [realTotalAmount, receiverId],
  );

  const handlePaySubmit = useCallback(
    async ({ tx }) => {
      mutateAsync({
        tx,
        receiverId,
      });
    },
    [mutateAsync, receiverId],
  );

  return (
    <L2PayModal
      turnOff={turnOff}
      txParams={txParams}
      payTitle={payTitle}
      modalClassName={style.modalClassName}
      onPaySubmit={handlePaySubmit}>
      <ConfirmPanel key={'confirm-panel'} />
    </L2PayModal>
  );
};

const ConfirmPanel = ({ showNext, onClose }: ModalStepProps<null>) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className={style.giftModal}>
        <div className={style.title}>{t('GiftWeb3IdEntry.157mt5n')}</div>
        <div className={style.content}>
          <div>{t('GiftWeb3IdEntry.1jdqk7c')}</div>
          <br />
          <div>{t('GiftWeb3IdEntry.1ssr9n0')}</div>
          <div>{t('GiftWeb3IdEntry.nxmhbn')}</div>
          <div>{t('GiftWeb3IdEntry.1f5w9jc')}</div>
          <br />

          <div>{t('GiftWeb3IdEntry.1byh0l3')}</div>
        </div>
        <div className={style.footer}>
          <Button type="default" className={style.footerButton} onClick={onClose}>
            {t('GiftWeb3IdEntry.x9d2fu')}
          </Button>
          <Button type="primary" className={style.footerButton} onClick={showNext}>
            {t('GiftWeb3IdEntry.4vrmbu')}
          </Button>
        </div>
      </div>
    </div>
  );
};
